/**
 * QR-Link-Format laut Konzept (Abschnitte „Domains“ und „QR-Codes und Freigaben“):
 * HTTPS://QR.SOMESUNNYMIND.COM/<KÜRZEL>/<SCHLÜSSEL>, ganz in Großbuchstaben,
 * Kürzel und Schlüssel nur aus Großbuchstaben und Ziffern.
 */

export const QR_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
export const KUERZEL_LAENGE = 3;
export const SCHLUESSEL_LAENGE = 10;

/** gross = ganzer Link in Großbuchstaben, gemischt = übliche Schreibweise mit kleiner Domain. */
export type QrVariante = 'gross' | 'gemischt';

export function qrLink(host: string, kuerzel: string, schluessel: string, variante: QrVariante): string {
  return variante === 'gross'
    ? `HTTPS://${host.toUpperCase()}/${kuerzel}/${schluessel}`
    : `https://${host.toLowerCase()}/${kuerzel}/${schluessel}`;
}

const PFAD_MUSTER = new RegExp(`^/([A-Z0-9]{${KUERZEL_LAENGE}})/([A-Z0-9]{${SCHLUESSEL_LAENGE}})/?$`, 'i');

/** Liest Kürzel und Schlüssel aus einem Pfad, unabhängig von Groß- und Kleinschreibung. */
export function parseQrPfad(pfad: string): { kuerzel: string; schluessel: string } | null {
  const ohneQuery = pfad.split(/[?#]/, 1)[0] ?? '';
  const treffer = PFAD_MUSTER.exec(ohneQuery);
  if (!treffer?.[1] || !treffer[2]) return null;
  return { kuerzel: treffer[1].toUpperCase(), schluessel: treffer[2].toUpperCase() };
}

export type ScanErgebnis =
  | { art: 'tfm'; host: string; kuerzel: string; schluessel: string }
  | { art: 'fremd'; text: string }
  | { art: 'kein-link'; text: string };

/**
 * Wertet den Inhalt eines gescannten QR-Codes aus, wie es der Scanner in der App
 * tut: Er liest Kürzel und Schlüssel direkt und akzeptiert nur bekannte QR-Domains.
 */
export function werteScanAus(text: string, bekannteHosts: readonly string[]): ScanErgebnis {
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    return { art: 'kein-link', text };
  }
  const host = url.hostname.toLowerCase();
  const bekannt = bekannteHosts.map((h) => h.toLowerCase()).includes(host);
  const teile = parseQrPfad(url.pathname);
  if (url.protocol.toLowerCase() !== 'https:' || !bekannt || !teile) return { art: 'fremd', text };
  return { art: 'tfm', host, ...teile };
}

export function zufallsCode(laenge: number, zufall: (n: number) => Uint8Array): string {
  // Verwerfen statt Modulo, damit jedes Zeichen gleich wahrscheinlich ist.
  const grenze = 256 - (256 % QR_ALPHABET.length);
  let ergebnis = '';
  while (ergebnis.length < laenge) {
    for (const byte of zufall(laenge * 2)) {
      if (byte < grenze) ergebnis += QR_ALPHABET[byte % QR_ALPHABET.length];
      if (ergebnis.length === laenge) break;
    }
  }
  return ergebnis;
}
